import { mat3, vec2 } from "gl-matrix";
import Graph from "graphology";
import { CONSOLE_STYLE_SUMMARY, logMeFnTitle, CONSOLE_STYLE_TITLE } from "../../shared/logger/logger";
import { Point } from "../Point";
import { transformPointByMatrix3 } from "../../shared/common";
import { FlowDirection } from "../../shared/types";
import { FittingMetadata, NodeAttributes } from "./GraphTypes";
import { getDisplayedShapes } from "../../shared/globalState/a";
import { extractNodeIdsWithFittings } from "../../services/network/NetworkHelpers";
import { Line } from "../shapes/Line";
import { Block } from "../shapes/Block";
import { ElbowBlock } from "../resistances/ElbowBlock";
import { Tee } from "../resistances/Tee";

const NODE_SIZE = 5;
const NODE_COLOR = 'blue';
const EDGE_SIZE = 2;
const EDGE_COLOR = 'red';
const EDGE_OPTIONS = {
    size: EDGE_SIZE,
    color: EDGE_COLOR
};

/**
 * Interface for metadata when adding fitting information to a node
 */
interface AddFittingMetadataParams {
    fittingId: string;
    fittingType: string;
    segmentEndpointIds?: string[];
}

interface EdgeMetadata {
    flowDirection: string;
}

/**
 * Extended Graph class with additional methods for network management
 */
export class Graf extends Graph {

    constructor() {
        super();
    }

    /**
     * Finds a graph that contains any of the given node IDs
     * @param graphs - Array of graphs to search
     * @param nodes - Array of node IDs to look for
     * @returns The found graph or undefined
     */
    static findGraph(graphs: Graf[], nodes: string[]): Graf | undefined {
        const graph = graphs.find(g => {
            for (const id of nodes) {
                // Check direct node ID or metadata IDs
                if (g.hasNode(id) || g.hasNodeByAnyId(id)) {
                    return true;
                }
            }
            return false;
        });
        return graph;
    }

    /**
     * Adds a node to the graph
     * @param point - Point object to add as a node
     * @returns The node key
     */
    override addNode(point: Point): string {
        if (!point) return '';

        const id = point.id;
        const x = point.x;
        const y = point.y;

        return super.addNode(id, {
            label: id,
            x: x,
            y: -y,
            size: NODE_SIZE,
            color: NODE_COLOR
        });
    }

    addNodeWithAttributes(point: Point, attributes: any): string {
        if (!point) return '';

        const id = point.id;
        const x = point.x;
        const y = point.y;

        return super.addNode(id, {
            ...attributes,
            x: x,
            y: y,
            size: NODE_SIZE,
            color: NODE_COLOR
        });
    }

    /**
     * Merges a node into the graph (adds if doesn't exist, updates if exists)
     * @param point - Point object to merge
     * @returns Merge result
     */
    mergeNode(point: Point): any {
        if (!point) return null;

        const id = point.id;
        const x = point.x;
        const y = point.y;

        return super.mergeNode(id, {
            label: id,
            x: x,
            y: -y,
            size: NODE_SIZE,
            color: NODE_COLOR
        });
    }

    /**
     * Adds an edge between two points
     * @param startPoint - Starting point
     * @param endPoint - Ending point
     * @returns The edge key
     */
    addEdge(startPoint: Point, endPoint: Point): string {
        if (!startPoint || !endPoint) return '';

        const startId = startPoint.id;
        const endId = endPoint.id;

        return super.addUndirectedEdge(startId, endId, EDGE_OPTIONS);
    }

    /**
     * Merges an edge into the graph
     * @param startId - ID of start node
     * @param endId - ID of end node
     * @returns Merge result
     */
    mergeEdge(startId: string, endId: string): any {
        return super.mergeEdge(startId, endId, EDGE_OPTIONS);
    }

    /**
     * Prints all nodes in the graph to console
     */
    printNodes(): void {
        console.log(`%c** nodes **`, CONSOLE_STYLE_TITLE);
        let counter = 0;
        const nodes = super.nodes();

        if (nodes?.length) {
            super.forEachNode(node => console.log(`Node ${counter++}:`, node));
        }
        console.log(`%cTotal nodes: ${counter}`, CONSOLE_STYLE_SUMMARY);
    }

    /**
     * Prints all edges in the graph to console
     */
    printEdges(): void {
        console.log(`%c** edges **`, CONSOLE_STYLE_TITLE);
        let counter = 0;
        const edges = super.edges();

        if (edges?.length) {
            super.forEachEdge(edge => console.log(`Edge ${counter++}:`, edge));
        }
        console.log(`%cTotal edges: ${counter}`, CONSOLE_STYLE_SUMMARY);
    }

    /**
     * Prints all nodes with their attributes in a formatted way
     * Shows node ID, parent/child IDs, label, isTerminal, isOpenEnd, and flowDirection
     */
    printNodesWithAttributes(): void {
        const NODE_STYLE_HEADER = 'font-size: 16px; font-weight: bold; background-color: #4CAF50; color: white; padding: 4px 8px; border-radius: 4px';
        const NODE_STYLE_LABEL = 'font-weight: bold; color: #2196F3';
        const NODE_STYLE_VALUE = 'color: #666';
        const NODE_STYLE_BOOLEAN_TRUE = 'color: #4CAF50; font-weight: bold';
        const NODE_STYLE_BOOLEAN_FALSE = 'color: #F44336; font-weight: bold';
        const NODE_STYLE_FLOW_IN = 'color: #00ff00; font-weight: bold';
        const NODE_STYLE_FLOW_OUT = 'color: #ff0000; font-weight: bold';
        const NODE_STYLE_FLOW_UNKNOWN = 'color: #808080; font-weight: bold';

        console.log(`%c** Graph Nodes with Attributes **`, CONSOLE_STYLE_TITLE);
        const nodes = super.nodes();

        if (!nodes || nodes.length === 0) {
            console.log(`%cNo nodes found`, NODE_STYLE_VALUE);
            return;
        }

        nodes.forEach((nodeId, index) => {
            const attrs = super.getNodeAttributes(nodeId) as NodeAttributes;
            
            // Extract child IDs from fittings
            const childIds: string[] = [];
            if (attrs.fittings && Array.isArray(attrs.fittings)) {
                attrs.fittings.forEach(fitting => {
                    if (fitting && typeof fitting === 'object' && 'childNodeId' in fitting) {
                        childIds.push(fitting.childNodeId);
                    }
                });
            }

            // Format flow direction with color
            const flowDirection = attrs.flowDirection || FlowDirection.UNKNOWN;
            let flowDirectionStyle = NODE_STYLE_FLOW_UNKNOWN;
            let flowDirectionText = 'UNKNOWN';
            if (flowDirection === FlowDirection.IN) {
                flowDirectionStyle = NODE_STYLE_FLOW_IN;
                flowDirectionText = 'IN';
            } else if (flowDirection === FlowDirection.OUT) {
                flowDirectionStyle = NODE_STYLE_FLOW_OUT;
                flowDirectionText = 'OUT';
            }

            // Print node header
            console.log(`%cNode ${index + 1}: ${nodeId}`, NODE_STYLE_HEADER);
            
            // Print parent ID (node ID itself)
            console.log(`  %cParent ID:%c ${nodeId}`, NODE_STYLE_LABEL, NODE_STYLE_VALUE);
            
            // Print child IDs
            if (childIds.length > 0) {
                console.log(`  %cChild IDs:%c [${childIds.join(', ')}]`, NODE_STYLE_LABEL, NODE_STYLE_VALUE);
            } else {
                console.log(`  %cChild IDs:%c none`, NODE_STYLE_LABEL, NODE_STYLE_VALUE);
            }
            
            // Print label
            console.log(`  %cLabel:%c ${attrs.label || 'N/A'}`, NODE_STYLE_LABEL, NODE_STYLE_VALUE);
            
            // Print isTerminal
            const isTerminal = attrs.isTerminal === true;
            console.log(`  %cisTerminal:%c ${isTerminal}`, NODE_STYLE_LABEL, isTerminal ? NODE_STYLE_BOOLEAN_TRUE : NODE_STYLE_BOOLEAN_FALSE);
            
            // Print isOpenEnd
            const isOpenEnd = attrs.isOpenEnd === true;
            console.log(`  %cisOpenEnd:%c ${isOpenEnd}`, NODE_STYLE_LABEL, isOpenEnd ? NODE_STYLE_BOOLEAN_TRUE : NODE_STYLE_BOOLEAN_FALSE);
            
            // Print flowDirection
            console.log(`  %cFlow Direction:%c ${flowDirectionText}`, NODE_STYLE_LABEL, flowDirectionStyle);
            
            console.log(''); // Empty line for spacing
        });

        console.log(`%cTotal nodes: ${nodes.length}`, CONSOLE_STYLE_SUMMARY);
    }

    /**
     * Applies zoom transformation to all nodes in the graph
     * @param zl - Zoom level multiplier
     */
    zoom(zl: number): void {
        const vec = vec2.fromValues(zl, zl);
        const zoomMat = mat3.fromScaling(mat3.create(), vec);
        const nodes = super.nodes();

        nodes.forEach(node => {
            const attr = super.getNodeAttributes(node) as NodeAttributes;

            const x = attr.x;
            const y = attr.y;

            const point = new Point(x, y);
            const newPoint = transformPointByMatrix3(zoomMat, point);

            const newX = newPoint.x;
            const newY = newPoint.y;

            super.setNodeAttribute(node, 'x', newX);
            super.setNodeAttribute(node, 'y', newY);
        });
    }

    /**
     * Applies pan transformation to all nodes in the graph
     * @param dx - Pan distance in x direction
     * @param dy - Pan distance in y direction
     */
    pan(dx: number, dy: number): void {
        const vec = vec2.fromValues(dx, dy);
        const panMat = mat3.fromTranslation(mat3.create(), vec);
        const nodes = super.nodes();

        mat3.transpose(panMat, panMat);

        nodes.forEach(node => {
            const attr = super.getNodeAttributes(node) as NodeAttributes;
            const x = attr.x;
            const y = attr.y;

            const point = new Point(x, y);
            const newPoint = transformPointByMatrix3(panMat, point);

            const newX = newPoint.x;
            const newY = newPoint.y;

            super.setNodeAttribute(node, 'x', newX);
            super.setNodeAttribute(node, 'y', newY);
        });
    }

    /**
     * Gets terminal paths in the graph (placeholder)
     */
    getTerminalPaths(): void {
        // TODO: Implement terminal paths detection
    }

    /**
     * Gets segments path from nodes and shapes (placeholder)
     * @param nodes - Array of nodes
     * @param shapes - Array of shapes
     */
    getSegmentsPath(nodes: any, shapes: any): void {
        // TODO: Implement segments path detection
    }

    /**
     * Removes a node from the graph
     * @param id - ID of the node to remove
     */
    removeNode(id: string): void {
        super.dropNode(id);
    }

    /**
     * Adds metadata to a node indicating it represents both a segment endpoint and a fitting
     * @param nodeId - The ID of the node to update
     * @param metadata - Metadata object containing fitting information
     */
    addFittingMetadata(nodeId: string, metadata: AddFittingMetadataParams): void {
        if (!this.hasNode(nodeId)) {
            console.warn(`!! addFittingMetadata: Node ${nodeId} not found in graph`);
            return;
        }

        const { fittingId, fittingType, segmentEndpointIds = [] } = metadata;

        // Get existing metadata or initialize
        const existingMetadata: FittingMetadata = (this.getNodeAttribute(nodeId, 'fittingMetadata') as FittingMetadata) || {
            fittingIds: [],
            fittingTypes: [],
            segmentEndpointIds: []
        };

        // Add fitting info if not already present
        if (!existingMetadata.fittingIds.includes(fittingId)) {
            existingMetadata.fittingIds.push(fittingId);
            existingMetadata.fittingTypes.push(fittingType);
        }

        // Add segment endpoint IDs
        segmentEndpointIds.forEach(id => {
            if (!existingMetadata.segmentEndpointIds.includes(id)) {
                existingMetadata.segmentEndpointIds.push(id);
            }
        });

        // Set the metadata
        this.setNodeAttribute(nodeId, 'fittingMetadata', existingMetadata);
        this.setNodeAttribute(nodeId, 'nodeType', 'merged');
    }

    addEdgeMetadata(startId: string, endId: string, metadata: EdgeMetadata): void {
        if (!this.hasEdge(startId, endId)) {
            return;
        }

        this.setEdgeAttribute(startId, endId, metadata);
    }

    removeEdgeMetadata(startId: string, endId: string): void {
        if (!this.hasEdge(startId, endId)) {
            return;
        }

        this.removeEdgeAttribute(startId, endId, 'edgeMetadata');
    }

    getEdgeMetadata(startId: string, endId: string): EdgeMetadata | null {
        if (!this.hasEdge(startId, endId)) {
            return null;
        }

        return this.getEdgeAttribute(startId, endId, 'edgeMetadata') as EdgeMetadata;
    }

    /**
     * Removes fitting metadata from a node
     * @param nodeId - The ID of the node to update
     * @param fittingId - ID of the fitting to remove
     */
    removeFittingMetadata(nodeId: string, fittingId: string): void {
        if (!this.hasNode(nodeId)) {
            return;
        }

        const metadata = this.getNodeAttribute(nodeId, 'fittingMetadata') as FittingMetadata;
        if (!metadata) return;

        const index = metadata.fittingIds.indexOf(fittingId);
        if (index > -1) {
            metadata.fittingIds.splice(index, 1);
            metadata.fittingTypes.splice(index, 1);
        }

        // If no more fittings, remove the metadata entirely
        if (metadata.fittingIds.length === 0) {
            this.removeNodeAttribute(nodeId, 'fittingMetadata');
            this.removeNodeAttribute(nodeId, 'nodeType');
        } else {
            this.setNodeAttribute(nodeId, 'fittingMetadata', metadata);
        }
    }

    /**
     * Finds a node by any of its associated IDs (primary ID, fitting ID, or segment endpoint ID)
     * @param searchId - ID to search for
     * @returns The node ID if found, null otherwise
     */
    findNodeByAnyId(searchId: string): string | null {
        // Check if it's a direct node ID
        if (this.hasNode(searchId)) {
            return searchId;
        }

        // Search through node metadata
        const nodes = this.nodes();
        for (const nodeId of nodes) {
            const metadata = this.getNodeAttribute(nodeId, 'fittingMetadata') as FittingMetadata;
            if (metadata) {
                // Check if searchId is in fittingIds
                if (metadata.fittingIds && metadata.fittingIds.includes(searchId)) {
                    return nodeId;
                }
                // Check if searchId is in segmentEndpointIds
                if (metadata.segmentEndpointIds && metadata.segmentEndpointIds.includes(searchId)) {
                    return nodeId;
                }
            }
        }

        return null;
    }

    /**
     * Checks if a node has any ID associated with it (including metadata IDs)
     * @param searchId - ID to search for
     * @returns True if the ID is found
     */
    hasNodeByAnyId(searchId: string): boolean {
        return this.findNodeByAnyId(searchId) !== null;
    }

    /**
     * Gets all IDs associated with a node (primary ID + metadata IDs)
     * @param nodeId - The primary node ID
     * @returns Array of all associated IDs
     */
    getNodeAllIds(nodeId: string): string[] {
        if (!this.hasNode(nodeId)) {
            return [];
        }

        const ids = [nodeId];
        const metadata = this.getNodeAttribute(nodeId, 'fittingMetadata') as FittingMetadata;

        if (metadata) {
            if (metadata.fittingIds) {
                ids.push(...metadata.fittingIds);
            }
            if (metadata.segmentEndpointIds) {
                ids.push(...metadata.segmentEndpointIds);
            }
        }

        return ids;
    }

    /**
     * Gets the related Line's node ID from a fitting node ID
     * @param fittingNodeId - The fitting node ID (e.g., from a Tee or Elbow)
     * @returns The Line's node ID, or null if not found
     */
    getLineNodeIdByFittingNodeId(fittingNodeId: string): string | null {
        // Find the node that contains this fitting ID in its fittings array
        const graphNodes = this.nodes();
        
        for (const nodeId of graphNodes) {
            const fittings = this.getNodeAttribute(nodeId, 'fittings') as any[];
            
            if (fittings && Array.isArray(fittings)) {
                // Check if any fitting in the array matches the fittingNodeId
                // Fittings use childNodeId as the primary property, but also check id for backward compatibility
                const hasMatchingFitting = fittings.some((fitting: any) => 
                    fitting.childNodeId === fittingNodeId || fitting.id === fittingNodeId
                );
                
                if (hasMatchingFitting) {
                    // Return this node's ID (which is the Line's node ID)
                    return nodeId;
                }
            }
        }
        
        return null;
    }

    /**
     * Gets fitting IDs from a node's metadata
     * @param nodeId - The node ID
     * @returns Array of fitting IDs at this node
     */
    getNodeFittingIds(nodeId: string): string[] {
        if (!this.hasNode(nodeId)) {
            return [];
        }

        const metadata = this.getNodeAttribute(nodeId, 'fittingMetadata') as FittingMetadata;
        return metadata?.fittingIds || [];
    }

    /**
     * Gets fitting types from a node's metadata
     * @param nodeId - The node ID
     * @returns Array of fitting types at this node
     */
    getNodeFittingTypes(nodeId: string): string[] {
        if (!this.hasNode(nodeId)) {
            return [];
        }

        const metadata = this.getNodeAttribute(nodeId, 'fittingMetadata') as FittingMetadata;
        return metadata?.fittingTypes || [];
    }

    /**
     * Finds all nodes that have a specific fitting ID in their metadata
     * @param fittingId - The fitting ID to search for
     * @returns Array of node IDs that contain this fitting
     */
    findNodesByFittingId(fittingId: string): string[] {
        const result: string[] = [];
        const nodes = this.nodes();

        for (const nodeId of nodes) {
            const metadata = this.getNodeAttribute(nodeId, 'fittingMetadata') as FittingMetadata;
            if (metadata?.fittingIds?.includes(fittingId)) {
                result.push(nodeId);
            }
        }

        return result;
    }

    /**
     * Checks if a node represents a merged node (has fitting metadata)
     * @param nodeId - The node ID
     * @returns True if this is a merged node
     */
    isMergedNode(nodeId: string): boolean {
        if (!this.hasNode(nodeId)) {
            return false;
        }

        const nodeType = this.getNodeAttribute(nodeId, 'nodeType');
        return nodeType === 'merged';
    }

    /**
     * Gets all network elements (Line, Block, ElbowBlock, Tee) that belong to this graph
     * @returns Array of network elements connected to this graph's nodes
     */
    getAllElements(): (Line | Block | ElbowBlock | Tee)[] {
        const elements: (Line | Block | ElbowBlock | Tee)[] = [];
        
        // Get all nodes from this graph
        const nodes = this.nodes();
        if (!nodes || nodes.length === 0) {
            return elements;
        }
        
        // Extract all IDs (including fitting IDs) from the graph nodes
        const uniqueIds = extractNodeIdsWithFittings(nodes, this, undefined);
        
        // Get all displayed shapes
        const displayedShapes = getDisplayedShapes() || [];
        if (!displayedShapes || displayedShapes.length === 0) {
            return elements;
        }
        
        // Find all elements that have any of the graph's node IDs
        for (let i = 0; i < displayedShapes.length; i++) {
            const element = displayedShapes[i] as any;
            
            // Check if element has hasGraphNode method (Line, Block, ElbowBlock, Tee have this)
            if (typeof element.hasGraphNode !== 'function') {
                continue;
            }
            
            // Check if element has any of the IDs (including fitting IDs)
            for (const searchId of uniqueIds) {
                if (element.hasGraphNode(searchId)) {
                    elements.push(element as Line | Block | ElbowBlock | Tee);
                    break; // Avoid adding the same element multiple times
                }
            }
        }
        
        return elements;
    }
}

